import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { toast } from "react-toastify";
import Pagination from "react-js-pagination";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../../icons/latest-logo.png";
import "./NutritionistAssessmentForm.css";

const BODY_ASSESSMENT_ROWS = [
  { key: "assessment_date", label: "Date" },
  { key: "weight", label: "Weight" },
  { key: "bmi", label: "BMI" },
  { key: "chest", label: "Chest" },
  { key: "belly", label: "Belly" },
  { key: "hips", label: "Hips" },
  { key: "arms", label: "Arms" },
  { key: "thighs", label: "Thighs" },
  { key: "fat", label: "Fat" },
  { key: "vf", label: "VF" },
];

const MEAL_ROWS = [
  { meal: "Breakfast", timeKey: "breakfast_time", specKey: "breakfast_spec" },
  { meal: "Lunch", timeKey: "lunch_time", specKey: "lunch_spec" },
  { meal: "Dinner", timeKey: "dinner_time", specKey: "dinner_spec" },
  { meal: "Snack", timeKey: "snack_time", specKey: "snack_spec" },
  { meal: "Munching", timeKey: "munching_time", specKey: "munching_spec" },
];

const MIN_ASSESSMENTS = 3;

const PLAN_OBJECTIVE_OPTIONS = [
  { id: "fat_loss", label: "Fat loss/weight loss" },
  { id: "muscle_strength", label: "Muscle and strength gain" },
  { id: "disease_management", label: "Disease management" },
];

const STRESS_OPTIONS = [
  { value: "Minimal", label: "a) Minimal" },
  { value: "Moderate", label: "b) Moderate" },
  { value: "Unbearable", label: "c) Unbearable" },
];

const ACTIVITY_OPTIONS = [
  { value: "Office Job (Sedentary)", label: "a) Office Job (Sedentary)" },
  { value: "Light exercise", label: "b) Light exercise" },
  { value: "Moderate exercise", label: "c) Moderate exercise" },
  { value: "Heavy exercise", label: "d) Heavy exercise" },
  { value: "Athlete", label: "e) Athlete" },
];

const PMH_ITEMS = [
  { key: "diabetes", label: "Diabetes type 1 or 2" },
  { key: "hypertension_cvd", label: "Hypertension or CVD" },
  { key: "polycystic_ovarian_syndrome", label: "Polycystic ovarian syndrome" },
  { key: "anemia", label: "Anemia" },
  { key: "ibs", label: "IBS" },
  { key: "h_pylori", label: "H. Pylori" },
];

const emptyBodyEntry = () => ({
  assessment_date: "",
  weight: "",
  bmi: "",
  chest: "",
  belly: "",
  hips: "",
  arms: "",
  thighs: "",
  fat: "",
  vf: "",
});

/** Normalize API assessment dates for <input type="date"> (YYYY-MM-DD). */
const formatAssessmentDate = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  // Already a calendar date from API (preferred).
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // Legacy ISO UTC (e.g. 2026-04-08T19:00:00.000000Z) → local calendar day.
  const m = moment(raw);
  if (!m.isValid()) return raw.substring(0, 10);
  return m.format("YYYY-MM-DD");
};

const emptyFormState = () => ({
  client_id: "",
  client_branch_id: "",
  client_name: "",
  name: "",
  age: "",
  height: "",
  gender: "",
  goal_fat_loss: "",
  goal_muscle_strength: "",
  goal_disease_management: "",
  plan_objectives: [],
  tried_diet_plans: "",
  gym_member: "",
  following_diet: "",
  undergoing_training: "",
  medicine_supplements: "",
  daily_dietary_intake: MEAL_ROWS.reduce((acc, row) => {
    acc[row.timeKey] = "";
    acc[row.specKey] = "";
    return acc;
  }, {}),
  daily_water_intake: "",
  disliked_foods: "",
  allergic_foods: "",
  preferred_foods: "",
  diabetes: "",
  hypertension_cvd: "",
  polycystic_ovarian_syndrome: "",
  anemia: "",
  ibs: "",
  h_pylori: "",
  muscle_pain: "",
  any_other_issue: "",
  stress_level: "",
  activity_level: "",
  body_entries: [emptyBodyEntry(), emptyBodyEntry(), emptyBodyEntry()],
});

const formStateFromApi = (data) => {
  const intake = data.daily_dietary_intake || {};
  const mergedIntake = MEAL_ROWS.reduce((acc, row) => {
    acc[row.timeKey] = intake[row.timeKey] || "";
    acc[row.specKey] = intake[row.specKey] || "";
    return acc;
  }, {});

  const entries =
    data.body_entries && data.body_entries.length > 0
      ? data.body_entries.map((e) => ({
          id: e.id,
          // Prefer calendar date (Y-m-d). Avoid substring on UTC ISO strings
          // like 2026-04-08T19:00:00.000000Z which shifts the day in PKT.
          assessment_date: formatAssessmentDate(e.assessment_date),
          weight: e.weight || "",
          bmi: e.bmi || "",
          chest: e.chest || "",
          belly: e.belly || "",
          hips: e.hips || "",
          arms: e.arms || "",
          thighs: e.thighs || "",
          fat: e.fat || "",
          vf: e.vf || "",
        }))
      : [];
  while (entries.length < MIN_ASSESSMENTS) entries.push(emptyBodyEntry());

  const clientName = data.client
    ? `${data.client.first_name || ""} ${data.client.last_name || ""}`.trim()
    : data.name || "";

  return {
    client_id: data.client_id || "",
    client_branch_id: data.branch_id || "",
    client_name: clientName,
    name: data.name || clientName,
    age: data.age || "",
    height: data.height || "",
    gender: data.gender || "",
    goal_fat_loss: data.goal_fat_loss || "",
    goal_muscle_strength: data.goal_muscle_strength || "",
    goal_disease_management: data.goal_disease_management || "",
    plan_objectives: data.plan_objectives || [],
    tried_diet_plans: data.tried_diet_plans || "",
    gym_member: data.gym_member || "",
    following_diet: data.following_diet || "",
    undergoing_training: data.undergoing_training || "",
    medicine_supplements: data.medicine_supplements || "",
    daily_dietary_intake: mergedIntake,
    daily_water_intake: data.daily_water_intake || "",
    disliked_foods: data.disliked_foods || "",
    allergic_foods: data.allergic_foods || "",
    preferred_foods: data.preferred_foods || "",
    diabetes: data.diabetes || "",
    hypertension_cvd: data.hypertension_cvd || "",
    polycystic_ovarian_syndrome: data.polycystic_ovarian_syndrome || "",
    anemia: data.anemia || "",
    ibs: data.ibs || "",
    h_pylori: data.h_pylori || "",
    muscle_pain: data.muscle_pain || "",
    any_other_issue: data.any_other_issue || "",
    stress_level: data.stress_level || "",
    activity_level: data.activity_level || "",
    body_entries: entries,
  };
};

const payloadFromFormState = (form, branch_id, nutritionist_id) => ({
  branch_id,
  nutritionist_id,
  client_id: form.client_id || null,
  name: form.name,
  age: form.age,
  height: form.height,
  gender: form.gender,
  goal_fat_loss: form.goal_fat_loss,
  goal_muscle_strength: form.goal_muscle_strength,
  goal_disease_management: form.goal_disease_management,
  plan_objectives: form.plan_objectives,
  tried_diet_plans: form.tried_diet_plans,
  gym_member: form.gym_member,
  following_diet: form.following_diet,
  undergoing_training: form.undergoing_training,
  medicine_supplements: form.medicine_supplements,
  daily_dietary_intake: form.daily_dietary_intake,
  daily_water_intake: form.daily_water_intake,
  disliked_foods: form.disliked_foods,
  allergic_foods: form.allergic_foods,
  preferred_foods: form.preferred_foods,
  diabetes: form.diabetes,
  hypertension_cvd: form.hypertension_cvd,
  polycystic_ovarian_syndrome: form.polycystic_ovarian_syndrome,
  anemia: form.anemia,
  ibs: form.ibs,
  h_pylori: form.h_pylori,
  muscle_pain: form.muscle_pain,
  any_other_issue: form.any_other_issue,
  stress_level: form.stress_level,
  activity_level: form.activity_level,
  body_entries: form.body_entries.map((e) => ({
    assessment_date: e.assessment_date || null,
    weight: e.weight,
    bmi: e.bmi,
    chest: e.chest,
    belly: e.belly,
    hips: e.hips,
    arms: e.arms,
    thighs: e.thighs,
    fat: e.fat,
    vf: e.vf,
  })),
});


/**
 * Portrait A4 PDF matching the paper questionnaire (2 pages).
 */
async function downloadNutritionistQuestionnairePdf(printRootEl, fileName) {
  if (!printRootEl) throw new Error("Print element not found");

  const pages = printRootEl.querySelectorAll(".naf-print-page");
  if (!pages.length) throw new Error("No print pages found");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: pages[i].scrollWidth,
      height: pages[i].scrollHeight,
      windowWidth: pages[i].scrollWidth,
      windowHeight: pages[i].scrollHeight,
    });

    addCanvasPages(pdf, canvas, pageWidth, pageHeight, i === 0);
  }

  const safeName = (fileName || "questionnaire").replace(/[^\w-]+/g, "_");
  pdf.save(`Nutritionist_Questionnaire_${safeName}.pdf`);
}

/** Tall pages are cut into full-width A4 slices so later assessments stay visible. */
function addCanvasPages(pdf, canvas, pageWidth, pageHeight, isFirstPage) {
  const imgH = (canvas.height * pageWidth) / canvas.width;

  const place = (source, heightMm, first) => {
    if (!first) pdf.addPage("a4", "portrait");
    pdf.addImage(source.toDataURL("image/jpeg", 0.88), "JPEG", 0, 0, pageWidth, heightMm);
  };

  if (imgH <= pageHeight + 0.5) {
    place(canvas, imgH, isFirstPage);
    return;
  }

  const pxPerMm = canvas.height / imgH;
  const slicePx = Math.max(1, Math.floor(pageHeight * pxPerMm));
  let y = 0;
  let sliceIndex = 0;

  while (y < canvas.height - 2) {
    const h = Math.min(slicePx, canvas.height - y);
    if (h < 8 && sliceIndex > 0) break;

    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = h;
    const ctx = slice.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
    place(slice, h / pxPerMm, isFirstPage && sliceIndex === 0);
    y += h;
    sliceIndex += 1;
  }
}

const display = (val) =>
  val != null && String(val).trim() !== "" ? String(val) : "";

const showDate = (value) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
};

const paperColumns = (entries) => {
  const cols = [...(entries || [])];
  while (cols.length < MIN_ASSESSMENTS) cols.push(emptyBodyEntry());
  return cols;
};

function SheetHeader() {
  return (
    <div className="naf-head">
      <img src={logo} alt="Vostro World" className="naf-logo" crossOrigin="anonymous" />
      <div className="naf-head-title">
        <div className="naf-questionnaire">QUESTIONNAIRE</div>
        <div className="naf-tagline">HEALTH | FITNESS | WELLNESS</div>
      </div>
    </div>
  );
}

function SheetFooter({ page }) {
  return (
    <div className="naf-foot">
      <span>VOSTRO WORLD • CLIENT ASSESSMENT FORM</span>
      <span>PAGE {page} OF 2</span>
    </div>
  );
}

function SectionBar({ children, note }) {
  return (
    <div className="naf-bar">
      <span>{children}</span>
      {note ? <em>{note}</em> : null}
    </div>
  );
}

function LineInput({ edit, value, onChange, type = "text" }) {
  if (!edit) {
    return <span className="naf-line">{type === "date" ? showDate(value) : display(value)}</span>;
  }
  return (
    <input
      className="naf-line-input"
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function Tick({ edit, checked, label, onToggle }) {
  const mark = <span className={`naf-box ${checked ? "is-on" : ""}`}>{checked ? "✓" : ""}</span>;
  if (!edit) {
    return (
      <span className="naf-tick">
        {mark}
        <span>{label}</span>
      </span>
    );
  }
  return (
    <button type="button" className="naf-tick" onClick={onToggle}>
      {mark}
      <span>{label}</span>
    </button>
  );
}

function YesNo({ edit, value, onChange }) {
  return (
    <span className="naf-yn">
      <Tick edit={edit} checked={value === "Yes"} label="Yes" onToggle={() => onChange(value === "Yes" ? "" : "Yes")} />
      <Tick edit={edit} checked={value === "No"} label="No" onToggle={() => onChange(value === "No" ? "" : "No")} />
    </span>
  );
}

function ClientPage({ form, edit, onChange, onBodyEntryChange }) {
  const set = (field, value) => onChange({ ...form, [field]: value });
  const objectives = form.plan_objectives || [];
  const columns = paperColumns(form.body_entries);

  const addAssessment = () => {
    onChange({
      ...form,
      body_entries: [...columns, emptyBodyEntry()],
    });
  };

  const removeAssessment = (index) => {
    if (columns.length <= MIN_ASSESSMENTS) return;
    const next = columns.filter((_, i) => i !== index);
    onChange({ ...form, body_entries: next });
  };

  const toggleObjective = (id) => {
    const next = objectives.includes(id)
      ? objectives.filter((x) => x !== id)
      : [...objectives, id];
    set("plan_objectives", next);
  };

  return (
    <>
      <p className="naf-disclaimer">
        THIS DOCUMENT IS ONLY TO BE USED UNDER NUTRITIONAL INFORMATION ON AND THUS
        CANNOT BE USED FOR ANY OTHER PURPOSE. THE CLIENT IS INFORMED ABOUT EVERYTHING
        AND IS IN AWARENESS OF OUR PROTOCOLS.
      </p>

      <SectionBar>CLIENT INFORMATION</SectionBar>
      <div className="naf-info-row">
        <label className="naf-box-field naf-grow">
          <span>NAME</span>
          <LineInput edit={edit} value={form.name} onChange={(v) => set("name", v)} />
        </label>
        <label className="naf-box-field naf-age">
          <span>AGE</span>
          <LineInput edit={edit} value={form.age} onChange={(v) => set("age", v)} />
        </label>
        <label className="naf-box-field naf-gender">
          <span>GENDER</span>
          <LineInput edit={edit} value={form.gender} onChange={(v) => set("gender", v)} />
        </label>
      </div>
      <label className="naf-box-field">
        <span>HEIGHT</span>
        <LineInput edit={edit} value={form.height} onChange={(v) => set("height", v)} />
      </label>

      <div className="naf-panel">
        <p>The objective of the plan: (please tick against the goal)</p>
        <div className="naf-ticks">
          {PLAN_OBJECTIVE_OPTIONS.map((opt) => (
            <Tick
              key={opt.id}
              edit={edit}
              checked={objectives.includes(opt.id)}
              label={opt.label}
              onToggle={() => toggleObjective(opt.id)}
            />
          ))}
        </div>
      </div>
      {(form.goal_fat_loss || form.goal_muscle_strength || form.goal_disease_management) ? (
        <div className="naf-split">
          <label className="naf-box-field">
            <span>FAT LOSS NOTE</span>
            <LineInput edit={edit} value={form.goal_fat_loss} onChange={(v) => set("goal_fat_loss", v)} />
          </label>
          <label className="naf-box-field">
            <span>MUSCLE GAIN NOTE</span>
            <LineInput edit={edit} value={form.goal_muscle_strength} onChange={(v) => set("goal_muscle_strength", v)} />
          </label>
          <label className="naf-box-field">
            <span>DISEASE MANAGEMENT NOTE</span>
            <LineInput edit={edit} value={form.goal_disease_management} onChange={(v) => set("goal_disease_management", v)} />
          </label>
        </div>
      ) : null}

      <SectionBar>BODY ASSESSMENT</SectionBar>
      <table className="naf-table naf-assess-table">
        <thead>
          <tr>
            {BODY_ASSESSMENT_ROWS.map((row) => (
              <th key={row.key}>{row.label}</th>
            ))}
            {edit && columns.length > MIN_ASSESSMENTS ? <th className="naf-assess-remove-h" /> : null}
          </tr>
        </thead>
        <tbody>
          {columns.map((entry, rowIdx) => (
            <tr key={rowIdx}>
              {BODY_ASSESSMENT_ROWS.map((row) => (
                <td key={row.key}>
                  <LineInput
                    edit={edit}
                    type={row.key === "assessment_date" ? "date" : "text"}
                    value={entry[row.key]}
                    onChange={(v) => onBodyEntryChange(rowIdx, row.key, v)}
                  />
                </td>
              ))}
              {edit && columns.length > MIN_ASSESSMENTS ? (
                <td className="naf-assess-remove-c">
                  <button
                    type="button"
                    className="naf-col-remove"
                    onClick={() => removeAssessment(rowIdx)}
                    aria-label={`Remove assessment ${rowIdx + 1}`}
                  >
                    ×
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {edit ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-dark naf-add-assessment"
          onClick={addAssessment}
        >
          <i className="bi bi-plus-lg me-1" />
          Add assessment
        </button>
      ) : null}

      <SectionBar>LIFESTYLE &amp; FITNESS PROFILE</SectionBar>
      <div className="naf-split">
        <label className="naf-box-field">
          <span>HAVE YOU TRIED ANY DIET PLANS BEFORE?</span>
          <LineInput edit={edit} value={form.tried_diet_plans} onChange={(v) => set("tried_diet_plans", v)} />
        </label>
        <label className="naf-box-field">
          <span>GYM MEMBER OR NOT:</span>
          <LineInput edit={edit} value={form.gym_member} onChange={(v) => set("gym_member", v)} />
        </label>
        <label className="naf-box-field">
          <span>ALREADY FOLLOWING ANY DIET:</span>
          <LineInput edit={edit} value={form.following_diet} onChange={(v) => set("following_diet", v)} />
        </label>
        <label className="naf-box-field">
          <span>UNDERGOING ANY TYPE OF TRAINING:</span>
          <LineInput edit={edit} value={form.undergoing_training} onChange={(v) => set("undergoing_training", v)} />
        </label>
      </div>
      <label className="naf-box-field">
        <span>CURRENTLY USING ANY MEDICINE OR SUPPLEMENTS:</span>
        <LineInput edit={edit} value={form.medicine_supplements} onChange={(v) => set("medicine_supplements", v)} />
      </label>
    </>
  );
}

function DietPage({ form, edit, onChange }) {
  const set = (field, value) => onChange({ ...form, [field]: value });
  const intake = form.daily_dietary_intake || {};
  const setIntake = (key, value) =>
    onChange({
      ...form,
      daily_dietary_intake: { ...intake, [key]: value },
    });

  return (
    <>
      <SectionBar>YOUR DAILY DIETARY INTAKE</SectionBar>
      <table className="naf-table naf-meals">
        <thead>
          <tr>
            <th>TIME</th>
            <th>MEALS</th>
            <th>SPECIFICATION</th>
          </tr>
        </thead>
        <tbody>
          {MEAL_ROWS.map((row) => (
            <tr key={row.meal}>
              <td>
                <LineInput
                  edit={edit}
                  value={intake[row.timeKey]}
                  onChange={(v) => setIntake(row.timeKey, v)}
                />
              </td>
              <td className="naf-meal-name">{row.meal}</td>
              <td>
                <LineInput
                  edit={edit}
                  value={intake[row.specKey]}
                  onChange={(v) => setIntake(row.specKey, v)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="naf-split">
        <label className="naf-box-field">
          <span>DAILY WATER INTAKE:</span>
          <LineInput edit={edit} value={form.daily_water_intake} onChange={(v) => set("daily_water_intake", v)} />
        </label>
        <label className="naf-box-field">
          <span>FOOD ALLERGIES IF ANY:</span>
          <LineInput edit={edit} value={form.allergic_foods} onChange={(v) => set("allergic_foods", v)} />
        </label>
        <label className="naf-box-field">
          <span>FOOD YOU DON&apos;T PREFER:</span>
          <LineInput edit={edit} value={form.disliked_foods} onChange={(v) => set("disliked_foods", v)} />
        </label>
        <label className="naf-box-field">
          <span>FOODS YOU PREFER:</span>
          <LineInput edit={edit} value={form.preferred_foods} onChange={(v) => set("preferred_foods", v)} />
        </label>
      </div>

      <SectionBar note="only to be filled by the consultant">P.M.H (IF ANY)</SectionBar>
      <table className="naf-table naf-pmh">
        <thead>
          <tr>
            <th>MEDICAL CONDITION / EVALUATION</th>
            <th>STATUS</th>
          </tr>
        </thead>
        <tbody>
          {PMH_ITEMS.map((item) => (
            <tr key={item.key}>
              <td>{item.label}</td>
              <td>
                <YesNo edit={edit} value={form[item.key]} onChange={(v) => set(item.key, v)} />
              </td>
            </tr>
          ))}
          <tr>
            <td>Joint mobility issue or muscle pain:</td>
            <td>
              <LineInput edit={edit} value={form.muscle_pain} onChange={(v) => set("muscle_pain", v)} />
            </td>
          </tr>
          <tr>
            <td>Any other issue (DISEASE OR SURGERY):</td>
            <td>
              <LineInput edit={edit} value={form.any_other_issue} onChange={(v) => set("any_other_issue", v)} />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="naf-panel">
        <p>
          Do you take stress (health-related, Job-related, financial, marriage, family
          related, interpersonal, and spiritual)?
        </p>
        <div className="naf-ticks">
          {STRESS_OPTIONS.map((opt) => (
            <Tick
              key={opt.value}
              edit={edit}
              checked={form.stress_level === opt.value}
              label={opt.label}
              onToggle={() => set("stress_level", form.stress_level === opt.value ? "" : opt.value)}
            />
          ))}
        </div>
      </div>

      <div className="naf-panel">
        <p>Activity Level</p>
        <div className="naf-ticks naf-ticks-wrap">
          {ACTIVITY_OPTIONS.map((opt) => (
            <Tick
              key={opt.value}
              edit={edit}
              checked={form.activity_level === opt.value}
              label={opt.label}
              onToggle={() => set("activity_level", form.activity_level === opt.value ? "" : opt.value)}
            />
          ))}
        </div>
      </div>

      <div className="naf-sign-row">
        <div className="naf-sign">
          <strong>CLIENT ACKNOWLEDGMENT</strong>
          <div>
            <span>Signature</span>
            <span className="naf-sign-line" />
            <span>Date: ____ / ____ / ________</span>
          </div>
        </div>
        <div className="naf-sign">
          <strong>CONSULTANT APPROVAL</strong>
          <div>
            <span>Signature</span>
            <span className="naf-sign-line" />
            <span>Date: ____ / ____ / ________</span>
          </div>
        </div>
      </div>
    </>
  );
}

function FormPrintLayout({ form }) {
  return (
    <div className="naf-print-root">
      <div className="naf-print-page">
        <SheetHeader />
        <ClientPage form={form} edit={false} onChange={() => {}} onBodyEntryChange={() => {}} />
        <SheetFooter page="1" />
      </div>
      <div className="naf-print-page">
        <SheetHeader />
        <DietPage form={form} edit={false} onChange={() => {}} />
        <SheetFooter page="2" />
      </div>
    </div>
  );
}

function FormEditor({ form, onChange, onBodyEntryChange }) {
  return (
    <div className="naf-sheet-stack">
      <section className="naf-sheet">
        <SheetHeader />
        <ClientPage
          form={form}
          edit
          onChange={onChange}
          onBodyEntryChange={onBodyEntryChange}
        />
        <SheetFooter page="1" />
      </section>
      <section className="naf-sheet">
        <SheetHeader />
        <DietPage form={form} edit onChange={onChange} />
        <SheetFooter page="2" />
      </section>
    </div>
  );
}

const calcAge = (birthday) => {
  if (!birthday) return "";
  const b = moment(birthday);
  if (!b.isValid()) return "";
  return String(moment().diff(b, "years"));
};

export function NutritionistAssessmentFormPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef(null);

  const { state } = useContext(NoteState);
  const { branch_id, UId } = state;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [form, setForm] = useState(emptyFormState());

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNew) loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!isNew || !location.state?.clientId) return;
    const prefillFromHub = async () => {
      const { clientId, clientName } = location.state;
      setClientSearch(clientName || "");
      try {
        const existing = await UserServices.getNutritionistAssessmentFormByClient(
          clientId,
          branch_id
        );
        if (existing.status === 200 && existing.data.status && existing.data.data) {
          navigate(`/nutritionist-assessment-form/${existing.data.data.id}`, {
            replace: true,
          });
          return;
        }
      } catch (err) {
        console.log(err);
      }
      await autofillProfileOnly(clientId, clientName);
    };
    prefillFromHub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, location.state?.clientId]);

  useEffect(() => {
    const onClick = (e) => {
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(e.target)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchClients = async () => {
    try {
      const res = await UserServices.getClientsName(branch_id);
      if (res.status === 200) {
        const list = res.data.data.map((c) => ({
          id: c.id,
          name: `${c.first_name} ${c.last_name}`,
          phone: c.phone,
          branch_id: c.branch_id || "",
        }));
        setClients(list);
        setFilteredClients(list);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const loadForm = async () => {
    setLoading(true);
    try {
      const res = await UserServices.getNutritionistAssessmentForm(id);
      if (res.status === 200 && res.data.status) {
        const data = res.data.data;
        setForm(formStateFromApi(data));
        if (data.client) {
          setClientSearch(
            `${data.client.first_name || ""} ${data.client.last_name || ""}`.trim()
          );
        } else if (data.name) {
          setClientSearch(data.name);
        }
      }
    } catch (err) {
      toast.error("Could not load questionnaire");
      navigate("/nutritionist-assessment-form");
    }
    setLoading(false);
  };

  const autofillProfileOnly = async (clientId, clientName, clientBranchId) => {
    let name = clientName || "";
    let age = "";
    let gender = "";
    let height = "";

    try {
      const profileRes = await UserServices.getClientProfileInfo(clientId);
      if (profileRes.status === 200 && profileRes.data.data?.length) {
        const c = profileRes.data.data[0];
        name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
        gender = c.gender || "";
        age = calcAge(c.birthday);
      }
    } catch (err) {
      console.log(err);
    }

    try {
      const preRes = await UserServices.getAssessmentDetailsById(clientId);
      if (preRes.status === 200 && preRes.data.data?.length) {
        const pre = preRes.data.data[0];
        if (pre.height) height = String(pre.height);
      }
    } catch (err) {
      console.log(err);
    }

    setForm({
      ...emptyFormState(),
      client_id: clientId,
      client_branch_id: clientBranchId || "",
      client_name: name,
      name,
      age,
      gender,
      height,
    });
  };

  const loadExistingForClient = async (clientId) => {
    const res = await UserServices.getNutritionistAssessmentFormByClient(
      clientId,
      branch_id
    );
    if (res.status === 200 && res.data.status && res.data.data) {
      return res.data.data;
    }
    return null;
  };

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearch(v);
    setShowClientDropdown(true);
    setFilteredClients(
      v.trim() === ""
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleSelectClient = async (client) => {
    setClientSearch(client.name);
    setShowClientDropdown(false);

    try {
      const existing = await loadExistingForClient(client.id);
      if (existing) {
        toast.info(
          "This client already has a questionnaire — loaded existing record."
        );
        if (isNew || String(id) !== String(existing.id)) {
          navigate(`/nutritionist-assessment-form/${existing.id}`, {
            replace: true,
          });
        } else {
          setForm(formStateFromApi(existing));
        }
        return;
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.log(err);
      }
    }

    await autofillProfileOnly(client.id, client.name, client.branch_id);
    if (!isNew) {
      toast.info("No previous questionnaire — starting fresh for this client.");
    }
  };

  const handleBodyEntryChange = (colIdx, key, value) => {
    setForm((prev) => {
      const entries = paperColumns(prev.body_entries).map((entry) => ({ ...entry }));
      entries[colIdx] = { ...entries[colIdx], [key]: value };
      return { ...prev, body_entries: entries };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = payloadFromFormState(
      form,
      branch_id || form.client_branch_id || null,
      UId
    );
    try {
      if (isNew) {
        const res = await UserServices.createNutritionistAssessmentForm(payload);
        if (
          (res.status === 201 || res.status === 200) &&
          res.data.status
        ) {
          toast.success(
            res.data.message || "Questionnaire saved"
          );
          navigate(`/nutritionist-assessment-form/${res.data.data.id}`);
        }
      } else {
        const res = await UserServices.updateNutritionistAssessmentForm(
          id,
          payload
        );
        if (res.status === 200 && res.data.status) {
          toast.success("Questionnaire updated");
          setForm(formStateFromApi(res.data.data));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    }
    setSaving(false);
  };

  const handleDownloadPdf = async () => {
    if (!printRef.current) return;
    setPdfLoading(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 150)));
      await downloadNutritionistQuestionnairePdf(
        printRef.current,
        form.name || "questionnaire"
      );
      toast.success("PDF downloaded");
    } catch (err) {
      console.log(err);
      toast.error("PDF download failed");
    }
    setPdfLoading(false);
  };

  if (loading) {
    return (
      <div className="site-main-design">
        <MenuButton />
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="site-main-design">
      <MenuButton />

      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="page-heading d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h3 className="mb-0">
                <i className="bi bi-journal-text me-2"></i>
                {isNew ? "New Assessment Questionnaire" : "Edit Questionnaire"}
              </h3>
              <div className="d-flex flex-wrap gap-2">
                <Link
                  to="/nutritionist-assessment-form"
                  className="btn btn-outline-secondary btn-sm"
                >
                  <i className="bi bi-arrow-left me-1"></i>Back
                </Link>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  {pdfLoading ? "Generating…" : "Download PDF"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <i className="bi bi-save me-1"></i>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white py-2">
            <h6 className="mb-0">
              <i className="bi bi-search me-2"></i>Select Client
            </h6>
          </div>
          <div className="card-body">
            <label className="form-label text-muted small mb-1">
              Search a client. If they already have a questionnaire, the saved
              form loads automatically (add new assessments there). Otherwise
              profile details auto-fill for a new questionnaire.
            </label>
            <div className="position-relative" ref={clientDropdownRef}>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-person"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or phone..."
                  value={clientSearch}
                  onChange={handleClientSearch}
                  onFocus={() => setShowClientDropdown(true)}
                  autoComplete="off"
                />
              </div>
              {showClientDropdown && filteredClients.length > 0 && (
                <ul
                  className="list-group position-absolute w-100 shadow mt-1"
                  style={{ zIndex: 1050, maxHeight: 220, overflowY: "auto" }}
                >
                  {filteredClients.slice(0, 50).map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="list-group-item list-group-item-action py-2"
                        onClick={() => handleSelectClient(c)}
                      >
                        {c.name}
                        {c.phone ? (
                          <small className="text-muted ms-2">{c.phone}</small>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <FormEditor
          form={form}
          onChange={setForm}
          onBodyEntryChange={handleBodyEntryChange}
        />
      </div>

      {/* Hidden print layout — paper style, no buttons, used only for PDF */}
      <div className="naf-print-host" aria-hidden="true">
        <div ref={printRef}>
          <FormPrintLayout form={form} />
        </div>
      </div>
    </div>
  );
}


export default function NutritionistAssessmentFormList() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [search, setSearch] = useState("");
  const [limit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const fetchForms = async () => {
    setLoading(true);
    try {
      // Always list by branch (not logged-in user). Forms are saved under the
      // real nutritionist (e.g. Samra 10416); F-11 Nutrition / Admin logins
      // must still see the full branch list.
      const params = {
        branch_id: branch_id || undefined,
        search: search || undefined,
        limit,
        page: currentPage,
      };
      const res = await UserServices.getNutritionistAssessmentForms(params);
      if (res.status === 200 && res.data.status) {
        setForms(Array.isArray(res.data.data) ? res.data.data : []);
        setTotalResults(res.data.pagination?.total_record ?? 0);
      } else {
        setForms([]);
        setTotalResults(0);
      }
    } catch (err) {
      console.log(err);
      setForms([]);
      setTotalResults(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, search, branch_id]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this questionnaire?")) return;
    try {
      await UserServices.deleteNutritionistAssessmentForm(id);
      toast.success("Deleted successfully");
      fetchForms();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const displayName = (row) => {
    if (row.name) return row.name;
    if (row.client) {
      return `${row.client.first_name || ""} ${row.client.last_name || ""}`.trim();
    }
    return "—";
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="page-heading d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h3 className="mb-0">
                <i className="bi bi-journal-text me-2"></i>
                Assessment Questionnaire
              </h3>
              <Link
                to="/nutritionist-assessment-form/new"
                className="btn btn-primary btn-sm"
              >
                <i className="bi bi-plus-lg me-1"></i>New Questionnaire
              </Link>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-5">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by name..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Client / Name</th>
                      <th>Assessments</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : forms.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No questionnaires yet.
                        </td>
                      </tr>
                    ) : (
                      forms.map((row, i) => (
                        <tr key={row.id}>
                          <td>{(currentPage - 1) * limit + i + 1}</td>
                          <td>{displayName(row)}</td>
                          <td>{row.body_entries?.length ?? 0}</td>
                          <td>
                            {row.updated_at
                              ? moment(row.updated_at).format("DD MMM YYYY")
                              : "—"}
                          </td>
                          <td>
                            <Link
                              to={`/nutritionist-assessment-form/${row.id}`}
                              className="btn btn-sm btn-outline-primary me-1"
                            >
                              Edit / View
                            </Link>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(row.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalResults > limit && (
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={limit}
                  totalItemsCount={totalResults}
                  pageRangeDisplayed={5}
                  onChange={(page) => setCurrentPage(page)}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
